import { IItem } from "../../models"
import {
    AccountCodeDTO,
    ActivityDTO,
    ApplicationDTO,
    ApplicationVersionDTO,
    BillTypeDTO,
    DisplayTypeDTO,
    FieldDefinitionDTO,
    ItemDTO,
    OptionDTO,
    OrganizationItemDTO,
    ProbabilityDTO,
    ImpactDTO,
    StatusDTO,
    Type,
    TypeModel,
    UserDTO,
    UserGroupDTO,
} from "../models/treeSelector/models"
import { GroupData, SelectableValue } from "./type"

export const filterQueryMapper = (
    filter: string,
    type: Type,
    isNestedEnabled: boolean,
): string => {
    if (!filter || filter.trim() === "") {
        return ""
    }

    if (typeHasGroups(type)) {
        if (isNestedEnabled) {
            let groupFilter = null
            let valueFilter = null
            const trimmedFilter = filter.trim()
            if (trimmedFilter.includes(":")) {
                const colonIndex = trimmedFilter.indexOf(":")
                const beforeColon = trimmedFilter
                    .substring(0, colonIndex)
                    .trim()
                const afterColon = trimmedFilter
                    .substring(colonIndex + 1)
                    .trim()

                if (beforeColon) {
                    groupFilter = beforeColon
                }

                if (afterColon) {
                    valueFilter = afterColon
                }
            } else {
                groupFilter = trimmedFilter
                valueFilter = trimmedFilter
            }
            let parsedFilter = ""

            if (groupFilter && valueFilter && groupFilter === valueFilter) {
                parsedFilter = `(contains(${selectorToGroupTypeMapper[type]}/Label, '${groupFilter}') or contains(Label, '${valueFilter}'))`
            } else {
                if (groupFilter) {
                    parsedFilter += `contains(${selectorToGroupTypeMapper[type]}/Label, '${groupFilter}')`
                }
                if (valueFilter) {
                    if (parsedFilter) {
                        parsedFilter += " and "
                    }
                    parsedFilter += `contains(Label, '${valueFilter}')`
                }
            }

            return parsedFilter
        }
        return `contains(Label, '${filter}') or contains(${selectorToGroupTypeMapper[type]}/Label, '${filter}')`
    }
    return `contains(Label,'${filter}')`
}

export function selectorModelMapper<T extends Type>(
    type: T,
): (data: TypeModel[T]) => SelectableValue & {
    groupData: undefined | GroupData
} {
    switch (type) {
        case "TF.AccountCodeItemField":
            return (data) => {
                const casted = data as AccountCodeDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ActivityItemField":
            return (data) => {
                const casted = data as ActivityDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ApplicationItemField":
            return (data) => {
                const casted = data as ApplicationDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ApplicationVersionItemField":
            return (data) => {
                const casted = data as ApplicationVersionDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: casted?.Application?.Id
                        ? {
                              id: casted?.Application?.Id,
                              label: casted?.Application?.Label ?? "",
                          }
                        : undefined,
                }
            }
        case "TF.BillTypeItemField":
            return (data) => {
                const casted = data as BillTypeDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.DisplayTypeItemField":
            return (data) => {
                const casted = data as DisplayTypeDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.FieldDefinition":
            return (data) => {
                const casted = data as FieldDefinitionDTO
                const parsedId = casted.Id?.startsWith("CF")
                    ? parseInt(casted.Id?.substring(2))
                    : parseInt(casted.Id?.substring(2)) * -1
                let parsedGroupId = 0
                if (casted.Group) {
                    parsedGroupId = casted.Group.Id.startsWith("CFG")
                        ? parseInt(casted.Group.Id.substring(3))
                        : parseInt(casted.Group.Id.substring(3)) * -1
                }

                return {
                    id: parsedId,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData:
                        parsedGroupId !== 0 && parsedGroupId
                            ? {
                                  id: parsedGroupId,
                                  label: casted.Group?.Label || "",
                              }
                            : undefined,
                }
            }
        case "TF.ImpactItemField":
            return (data) => {
                const casted = data as ImpactDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ItemItemField":
        case "TF.ItemsItemField":
            return (data) => {
                const casted = data as ItemDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: true,
                    groupData: undefined,
                }
            }
        case "TF.OptionItemField":
        case "TF.OptionsItemField":
            return (data) => {
                const casted = data as OptionDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: casted?.Group?.Id
                        ? {
                              id: casted?.Group?.Id,
                              label: casted?.Group?.Label ?? "",
                          }
                        : undefined,
                }
            }
        case "TF.OrganizationItemField":
            return (data) => {
                const casted = data as OrganizationItemDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.ProbabilityItemField":
            return (data) => {
                const casted = data as ProbabilityDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.PriorityItemField":
        case "TF.StatusItemField":
            return (data) => {
                const casted = data as StatusDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                    isIndicatingItemIsActive: casted.IndicatesItemIsActive,
                }
            }
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return (data) => {
                const casted = data as UserGroupDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: undefined,
                }
            }
        case "TF.UserItemField":
        case "TF.UsersItemField":
            return (data) => {
                const casted = data as UserDTO
                return {
                    id: casted.Id,
                    label: casted.Label,
                    isActive: casted.IsActive,
                    groupData: casted.OrganizationUnit?.Id
                        ? {
                              id: casted.OrganizationUnit?.Id,
                              label: casted.OrganizationUnit?.Label || "",
                          }
                        : undefined,
                }
            }

        default:
            return (data) => ({
                id: data.Id as number,
                label: "",
                isActive: true,
                groupData: undefined,
            })
    }
}

export const selectorToSingularTypeMapper: Record<Type, string> = {
    "TF.AccountCodeItemField": "TF.AccountCode",
    "TF.ActivityItemField": "TF.Activity",
    "TF.ApplicationItemField": "TF.Application",
    "TF.ApplicationVersionItemField": "TF.ApplicationVersion",
    "TF.BillTypeItemField": "TF.BillType",
    "TF.DisplayTypeItemField": "TF.DisplayType",
    "TF.FieldDefinition": "TF.FieldDefinition",
    "TF.ImpactItemField": "TF.Impact",
    "TF.ItemItemField": "TF.Item",
    "TF.ItemsItemField": "TF.Item",
    "TF.OptionItemField": "TF.Option",
    "TF.OptionsItemField": "TF.Option",
    "TF.OrganizationItemField": "TF.OrganizationUnit",
    "TF.ProbabilityItemField": "TF.Probability",
    "TF.PriorityItemField": "TF.ItemPriority",
    "TF.StatusItemField": "TF.ItemStatus",
    "TF.UserGroupItemField": "TF.UserGroup",
    "TF.UserGroupsItemField": "TF.UserGroup",
    "TF.UserItemField": "TF.User",
    "TF.UsersItemField": "TF.User",
} as const

export const selectorToGroupTypeMapper: Record<Type, string | undefined> = {
    "TF.AccountCodeItemField": undefined,
    "TF.ActivityItemField": undefined,
    "TF.ApplicationItemField": undefined,
    "TF.ApplicationVersionItemField": "Application",
    "TF.BillTypeItemField": undefined,
    "TF.DisplayTypeItemField": undefined,
    "TF.FieldDefinition": undefined,
    "TF.ImpactItemField": undefined,
    "TF.ItemItemField": undefined,
    "TF.ItemsItemField": undefined,
    "TF.OptionItemField": "Group",
    "TF.OptionsItemField": "Group",
    "TF.OrganizationItemField": undefined,
    "TF.ProbabilityItemField": undefined,
    "TF.PriorityItemField": undefined,
    "TF.StatusItemField": undefined,
    "TF.UserGroupItemField": undefined,
    "TF.UserGroupsItemField": undefined,
    "TF.UserItemField": "OrganizationUnit",
    "TF.UsersItemField": "OrganizationUnit",
}

export const typeHasGroups = (type: Type): boolean => {
    switch (type) {
        case "TF.ApplicationVersionItemField":
        case "TF.OptionItemField":
        case "TF.OptionsItemField":
        case "TF.UserItemField":
        case "TF.UsersItemField":
            return true
        case "TF.AccountCodeItemField":
        case "TF.ActivityItemField":
        case "TF.ApplicationItemField":
        case "TF.BillTypeItemField":
        case "TF.DisplayTypeItemField":
        case "TF.FieldDefinition":
        case "TF.ImpactItemField":
        case "TF.ItemItemField":
        case "TF.ItemsItemField":
        case "TF.OrganizationItemField":
        case "TF.ProbabilityItemField":
        case "TF.PriorityItemField":
        case "TF.StatusItemField":
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return false
    }
}

export const parseItemPickerData = (response: ItemDTO[] | ItemDTO): IItem[] => {
    if (!response) {
        return []
    }

    const responseChildren = Array.isArray(response) ? response : [response]
    return responseChildren.map((child) => {
        return {
            id: child.Id,
            title: child.Label,
            number: child.Number,
        }
    })
}

export const orderQueryMapper = (type: Type): string => {
    switch (type) {
        case "TF.AccountCodeItemField":
        case "TF.ActivityItemField":
        case "TF.ApplicationItemField":
        case "TF.BillTypeItemField":
        case "TF.DisplayTypeItemField":
        case "TF.OrganizationItemField":
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return "Label"

        case "TF.ImpactItemField":
        case "TF.ProbabilityItemField":
        case "TF.PriorityItemField":
        case "TF.StatusItemField":
            return "SequenceNr, Label"

        case "TF.ApplicationVersionItemField":
            return "Application/Label, SequenceNr, Label"

        case "TF.OptionItemField":
        case "TF.OptionsItemField":
            return "Group/Label, SequenceNr, Label"

        case "TF.UserItemField":
        case "TF.UsersItemField":
            return "OrganizationUnit/Label, Label"
        case "TF.ItemItemField":
        case "TF.ItemsItemField":
            return "Label"
        default:
            return ""
    }
}

export const typeQueryMapper = (type: Type): string => {
    switch (type) {
        case "TF.AccountCodeItemField":
            return "accountcodes"
        case "TF.ActivityItemField":
            return "activities"
        case "TF.ApplicationItemField":
            return "applications"
        case "TF.ApplicationVersionItemField":
            return "applicationVersions"
        case "TF.BillTypeItemField":
            return "billTypes"
        case "TF.DisplayTypeItemField":
            return "displayTypes"
        case "TF.FieldDefinition":
            return "fieldDefinitions"
        case "TF.ImpactItemField":
            return "impacts"
        case "TF.OrganizationItemField":
            return "organizationUnits"
        case "TF.PriorityItemField":
            return "itemPriorities"
        case "TF.ProbabilityItemField":
            return "probabilities"
        case "TF.StatusItemField":
            return "itemStatuses"
        case "TF.UserItemField":
        case "TF.UsersItemField":
            return "users"
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return "userGroups"
        case "TF.OptionItemField":
        case "TF.OptionsItemField":
            return "options"
        case "TF.ItemItemField":
        case "TF.ItemsItemField":
            return "items"
        default:
            return ""
    }
}
